import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Radar, Bell, Newspaper, ClipboardCheck, FolderKanban, Boxes, Cpu, ArrowRight, Loader2, Check, CheckCheck, Sparkles } from "lucide-react";

// ArcaCentum Notification Center — persisted per-user read state + categories.
// The health dot reflects company health; the bell + count show UNREAD notifications.
// Clicking opens a premium, category-filterable dropdown with per-item + bulk
// "mark read". Self-contained (Studio + ArcaOS); reads auth/org from localStorage.
const LEVEL_LABEL = { green: "Healthy", blue: "Informational", yellow: "Needs attention", orange: "High priority", red: "Critical" };
const CATS = [
  { key: "all", label: "All", icon: Bell },
  { key: "attention", label: "AI Attention", icon: Radar },
  { key: "brief", label: "Daily Brief", icon: Newspaper },
  { key: "approvals", label: "Approvals", icon: ClipboardCheck },
  { key: "projects", label: "Projects", icon: FolderKanban },
  { key: "products", label: "Products", icon: Boxes },
  { key: "system", label: "System", icon: Cpu },
];
const CAT_META = Object.fromEntries(CATS.map((c) => [c.key, c]));

const authHeaders = (json) => {
  const token = localStorage.getItem("arca_token");
  if (!token) return null;
  const h = { Authorization: `Bearer ${token}` };
  const org = localStorage.getItem("arca_os_org");
  if (org) h["X-Org-Id"] = org;
  if (json) h["Content-Type"] = "application/json";
  return h;
};
const API = () => process.env.REACT_APP_BACKEND_URL;
const sevColor = (tag) => (["Critical", "Blocked"].includes(tag) ? "#F6465D" : ["High", "Risk", "Warning"].includes(tag) ? "#F59E0B" : "#00F0FF");

export default function AttentionPulse({ className = "" }) {
  const navigate = useNavigate();
  const [pulse, setPulse] = useState(null);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [byCat, setByCat] = useState({});
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const loadSummary = useCallback(async () => {
    const h = authHeaders();
    if (!h) { setPulse(null); return; }
    try {
      const r = await fetch(`${API()}/api/os/notify/summary`, { headers: h });
      if (r.ok) { const d = await r.json(); setPulse(d); setUnread(d.unread || 0); } else setPulse(null);
    } catch { setPulse(null); }
  }, []);

  const loadCenter = useCallback(async () => {
    const h = authHeaders();
    if (!h) return;
    setLoading(true);
    try {
      const r = await fetch(`${API()}/api/os/notify/center`, { headers: h });
      if (r.ok) { const d = await r.json(); setItems(d.items || []); setByCat(d.unread_by_category || {}); setUnread(d.unread || 0); if (d.pulse) setPulse((p) => ({ ...(p || {}), ...d.pulse, unread: d.unread })); }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadSummary(); const t = setInterval(loadSummary, 60000); return () => clearInterval(t); }, [loadSummary]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, []);

  const toggle = () => { const n = !open; setOpen(n); if (n) { setCat("all"); loadCenter(); } };

  const markRead = async (id, e) => {
    if (e) e.stopPropagation();
    const it = items.find((x) => x.id === id);
    if (!it || it.read) return;
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, read: true } : x)));
    setUnread((u) => Math.max(0, u - 1));
    setByCat((b) => ({ ...b, [it.category]: Math.max(0, (b[it.category] || 1) - 1) }));
    try { await fetch(`${API()}/api/os/notify/${id}/read`, { method: "POST", headers: authHeaders() }); } catch { /* optimistic */ }
  };

  const markAll = async () => {
    const scope = cat === "all" ? undefined : cat;
    setItems((arr) => arr.map((x) => (!scope || x.category === scope ? { ...x, read: true } : x)));
    if (scope) { setUnread((u) => Math.max(0, u - (byCat[scope] || 0))); setByCat((b) => ({ ...b, [scope]: 0 })); }
    else { setUnread(0); setByCat({}); }
    try { await fetch(`${API()}/api/os/notify/read-all`, { method: "POST", headers: authHeaders(true), body: JSON.stringify({ category: scope }) }); } catch { /* optimistic */ }
  };

  const openItem = (it) => { markRead(it.id); setOpen(false); navigate(it.link || "/os/attention"); };

  if (!pulse) return null;
  const color = pulse.color;
  const label = LEVEL_LABEL[pulse.level] || "";
  const shown = cat === "all" ? items : items.filter((i) => i.category === cat);

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button onClick={toggle} title={`Company health: ${label} · ${unread} unread`}
        className="group relative inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 pl-2 pr-3 py-1.5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF]"
        data-testid="attention-pulse" aria-label={`Notifications, ${unread} unread`} aria-haspopup="true" aria-expanded={open}>
        <span className="relative flex h-2.5 w-2.5">
          {pulse.level !== "green" && <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ backgroundColor: color }} />}
          <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: color }} data-testid="attention-pulse-dot" />
        </span>
        <Bell size={14} className="text-slate-300 group-hover:text-white transition-colors" />
        {unread > 0 && <span className="text-[11px] font-mono font-bold text-white" data-testid="attention-pulse-count">{unread > 99 ? "99+" : unread}</span>}
      </button>

      {open && (
        <div style={{ backgroundColor: "#0a0a0c" }} className="fixed left-2 right-2 top-16 w-auto max-h-[80vh] sm:absolute sm:left-auto sm:right-0 sm:rtl:right-auto sm:rtl:left-0 sm:top-auto sm:mt-3 sm:w-[27rem] sm:max-w-[93vw] sm:max-h-[85vh] flex flex-col rounded-2xl border border-white/10 shadow-2xl shadow-black/60 z-[80] overflow-hidden" data-testid="notification-center-panel">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-2">
              <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: color }} />
              <span className="text-sm font-display font-bold text-white">Notifications</span>
              {unread > 0 && <span className="text-[10px] font-mono text-[#00F0FF]">{unread} unread</span>}
            </div>
            <div className="flex items-center gap-2">
              {loading && <Loader2 size={13} className="animate-spin text-[#00F0FF]" />}
              <button onClick={markAll} className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-slate-300 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] rounded px-1 transition-colors" data-testid="notif-mark-all-read"><CheckCheck size={13} /> Mark all read</button>
            </div>
          </div>

          {/* Category filter chips */}
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/10 overflow-x-auto shrink-0" data-testid="notif-category-filters">
            {CATS.map((c) => {
              const n = c.key === "all" ? unread : (byCat[c.key] || 0);
              const active = cat === c.key;
              return (
                <button key={c.key} onClick={() => setCat(c.key)} data-testid={`notif-cat-${c.key}`} aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] transition-colors outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] ${active ? "bg-[#0055FF] text-white" : "bg-white/5 text-slate-300 hover:text-white"}`}>
                  <c.icon size={12} /> {c.label}{n > 0 && <span className={`ml-0.5 ${active ? "text-white" : "text-[#00F0FF]"}`}>{n}</span>}
                </button>
              );
            })}
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto">
            {shown.length === 0 ? <p className="px-4 py-8 text-center text-xs text-slate-400" data-testid="notif-empty">You&apos;re all caught up.</p>
              : shown.map((it) => {
                const Icon = (CAT_META[it.category] || {}).icon || Bell;
                return (
                  <div key={it.id} onClick={() => openItem(it)}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); openItem(it); } }}
                    aria-label={`${it.read ? "" : "Unread. "}${it.title}. Open`}
                    data-testid={`notif-item-${it.id}`}
                    className={`group/item flex items-start gap-3 px-4 py-3 border-b border-white/5 cursor-pointer transition-colors outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#00F0FF] ${it.read ? "hover:bg-white/[0.03]" : "bg-white/[0.02] hover:bg-white/[0.06]"}`}>
                    <span className="mt-0.5 shrink-0">
                      {it.category === "attention"
                        ? <span className="block h-2.5 w-2.5 rounded-full mt-1" style={{ backgroundColor: sevColor(it.tag) }} />
                        : it.actor_kind === "ai_agent" ? <Sparkles size={15} className="text-[#00F0FF]" /> : <Icon size={15} className="text-slate-300" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm truncate ${it.read ? "text-slate-400" : "text-white font-medium"}`}>{it.title}</span>
                        {it.tag && it.category === "attention" && <span className="text-[9px] font-mono uppercase text-slate-400 shrink-0">{it.tag}</span>}
                      </div>
                      <p className="text-xs text-slate-400 line-clamp-2">{it.body}</p>
                    </div>
                    {!it.read
                      ? <button onClick={(e) => markRead(it.id, e)} title="Mark as read" aria-label={`Mark "${it.title}" as read`} data-testid={`notif-mark-read-${it.id}`} className="shrink-0 inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-300 hover:text-[#00F0FF] hover:bg-white/10 outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] transition-colors"><Check size={15} /></button>
                      : <span className="shrink-0 w-6" />}
                    {!it.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#00F0FF] shrink-0" data-testid="notif-unread-dot" />}
                  </div>
                );
              })}
          </div>

          {/* Footer */}
          <div className="p-2.5 border-t border-white/10 bg-black/40 shrink-0">
            <button onClick={() => { setOpen(false); navigate("/os/attention"); }} className="w-full inline-flex items-center justify-center gap-2 bg-[#0055FF] hover:bg-[#0044dd] text-white text-sm font-semibold rounded-xl py-2.5 outline-none focus-visible:ring-2 focus-visible:ring-[#00F0FF] focus-visible:ring-offset-2 focus-visible:ring-offset-black transition-colors" data-testid="notif-open-attention">
              Open Attention Center <ArrowRight size={15} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
